import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CreditCard, Loader2, LogOut, LayoutDashboard, Users, Monitor, Megaphone, Calendar, CheckSquare, BarChart3, Plus, Send, CheckCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ParkWiseLogo } from "../../../components/ui/parkwise-logo";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const API = "/api";

const navItems = [
  { path: "/admin/advertising", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/advertising/advertisers", icon: Users, label: "Advertisers" },
  { path: "/admin/advertising/screens", icon: Monitor, label: "Screens & Inventory" },
  { path: "/admin/advertising/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/admin/advertising/calendar", icon: Calendar, label: "Bookings Calendar" },
  { path: "/admin/advertising/approvals", icon: CheckSquare, label: "Artwork Approvals" },
  { path: "/admin/advertising/proof-of-play", icon: BarChart3, label: "Proof of Play" },
  { path: "/admin/advertising/billing", icon: CreditCard, label: "Billing & Invoices" },
];

const statusColors = {
  draft: "bg-slate-600",
  sent: "bg-blue-600",
  paid: "bg-green-600",
  overdue: "bg-red-600",
  cancelled: "bg-gray-600"
};

export default function AdvertisingBilling() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");

  useEffect(() => { checkAuth(); }, []);
  useEffect(() => { if (user) { fetchInvoices(); fetchRevenueSummary(); } }, [user, statusFilter]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) { navigate("/admin/login"); }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/advertising/invoices`, { withCredentials: true });
      let filteredInvoices = response.data;
      
      // Filter client-side for outstanding (sent + overdue)
      if (statusFilter === "outstanding") {
        filteredInvoices = response.data.filter(inv => inv.status === "sent" || inv.status === "overdue");
      } else if (statusFilter !== "all") {
        filteredInvoices = response.data.filter(inv => inv.status === statusFilter);
      }
      
      setInvoices(filteredInvoices);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally { setLoading(false); }
  };

  const fetchRevenueSummary = async () => {
    try {
      const response = await axios.get(`${API}/advertising/revenue/summary`, { withCredentials: true });
      setRevenueSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch revenue summary:", error);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/advertising/invoices/${invoiceId}/mark-paid`, {}, { withCredentials: true });
      fetchInvoices();
      fetchRevenueSummary();
    } catch (error) {
      alert("Failed to mark invoice as paid");
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    navigate("/admin/login");
  };

  if (!user) {
    return (<div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>);
  }

  // Reconciliation counts
  const paidCount = invoices.filter(i => i.status === 'paid').length;
  const outstandingCount = invoices.filter(i => i.status === 'sent' || i.status === 'overdue').length;

  return (
    <div className="min-h-screen bg-slate-950 flex">
      <aside className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800"><ParkWiseLogo size="md" /><p className="text-slate-500 text-xs mt-2">Advertising & Campaigns</p></div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (<button key={item.path} onClick={() => navigate(item.path)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? "bg-blue-600/20 text-blue-400" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}><item.icon className="w-5 h-5" /><span className="text-sm font-medium">{item.label}</span></button>);
          })}
          <div className="pt-4 border-t border-slate-800 mt-4">
            <button onClick={() => navigate("/admin/dashboard")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800"><LayoutDashboard className="w-5 h-5" /><span className="text-sm">← Back to Main Admin</span></button>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2"><ParkWiseLogo size="sm" /><div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-xs text-slate-500 truncate">{user?.email}</p></div></div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full px-2"><LogOut className="w-4 h-4" /><span>Sign out</span></button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div><h1 className="text-2xl font-bold text-white">Billing & Invoices</h1><p className="text-slate-400 mt-1">Manage advertising invoices and payments</p></div>
            <Button className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" />Create Invoice</Button>
          </div>

          {/* Revenue Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Revenue This Month</p>
                <p className="text-2xl font-bold text-emerald-400">£{(revenueSummary?.revenue_this_month || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Revenue This Year</p>
                <p className="text-2xl font-bold text-white">£{(revenueSummary?.revenue_this_year || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Outstanding</p>
                <p className="text-2xl font-bold text-amber-400">£{(revenueSummary?.outstanding_value || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-500">{revenueSummary?.outstanding_count || 0} invoices</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Total Invoices</p>
                <p className="text-2xl font-bold text-white">{invoices.length}</p>
                <p className="text-xs text-slate-500">{paidCount} paid | {outstandingCount} outstanding</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="mb-6 flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="outstanding">Outstanding</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Invoices Table */}
          <Card className="bg-slate-900 border-slate-800">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>
            ) : invoices.length === 0 ? (
              <CardContent className="py-12 text-center">
                <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No invoices found.</p>
              </CardContent>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Invoice</TableHead>
                    <TableHead className="text-slate-400">Advertiser</TableHead>
                    <TableHead className="text-slate-400">Amount</TableHead>
                    <TableHead className="text-slate-400">Due Date</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map(inv => (
                    <TableRow key={inv.invoice_id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell>
                        <p className="text-white font-medium">{inv.invoice_id}</p>
                        <p className="text-xs text-slate-500">{inv.description || "Advertising services"}</p>
                      </TableCell>
                      <TableCell className="text-slate-300">{inv.advertiser_name}</TableCell>
                      <TableCell className="text-emerald-400 font-medium">£{(inv.amount || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-slate-400">{inv.due_date?.split('T')[0]}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs text-white ${statusColors[inv.status] || 'bg-slate-600'}`}>
                          {inv.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {inv.status === 'draft' && (
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-blue-400"><Send className="w-4 h-4" /></Button>
                          )}
                          {(inv.status === 'sent' || inv.status === 'overdue') && (
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-green-400" onClick={() => handleMarkPaid(inv.invoice_id)}><CheckCircle className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
